import { memo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { IconInfo, Skeleton, Text, XStack, tokens } from '@fhs/ui';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import {
  CHECKOUT_ORDER_SUMMARY_DELIVERY_BASE_FEE,
  CHECKOUT_ORDER_SUMMARY_DELIVERY_FEES,
  CHECKOUT_ORDER_SUMMARY_DELIVERY_GEOGRAPHICAL_FEE,
  CHECKOUT_ORDER_SUMMARY_DELIVERY_LONG_DISTANCE_FEE,
  SHOW_DELIVERY_FEE_INFO,
} from '@fhs-legacy/frontend/src/utils/test-ids';

import DeliveryFeesInfoModal from './delivery-fees-modal';

function DeliveryFees() {
  const { serverOrder } = useCartContext();
  const { isDelivery } = useServiceModeContext();
  const enableDeliveryFeesInfoButton = useFlag('show-delivery-fees-info');
  const [showDeliveryFeesInfo, setShowDeliveryFeesInfo] = useState(false);

  const deliveryOrder = serverOrder?.delivery;

  // show null for pick up mode
  if (!isDelivery) {
    return null;
  }

  // If service mode is delivery but no delivery order object yet, show skeleton because we are waiting for the response
  if (!deliveryOrder) {
    return (
      <View testID={CHECKOUT_ORDER_SUMMARY_DELIVERY_FEES} style={styles.deliveryContainer}>
        <Text.Paragraph size="md">Delivery Fees</Text.Paragraph>
        <View style={styles.deliveryFee}>
          <Text.Paragraph size="md">Base Fee</Text.Paragraph>
          <Skeleton style={styles.skeleton} />
        </View>
      </View>
    );
  }

  const baseFee = deliveryOrder.baseDeliveryFeeCents || 0;
  const longDistance = deliveryOrder.deliverySurchargeFeeCents || 0;
  const geographical = deliveryOrder.geographicalFeeCents || 0;

  return (
    <>
      <View testID={CHECKOUT_ORDER_SUMMARY_DELIVERY_FEES} style={styles.deliveryContainer}>
        <XStack style={{ alignItems: 'center', gap: 8 }}>
          <Text.Paragraph size="md">Delivery Fees</Text.Paragraph>
          {enableDeliveryFeesInfoButton && (
            <Pressable
              onPress={() => setShowDeliveryFeesInfo(true)}
              accessibilityRole="button"
              accessibilityLabel="Show delivery fees information"
              testID={SHOW_DELIVERY_FEE_INFO}
            >
              <IconInfo size={20} />
            </Pressable>
          )}
        </XStack>

        <View style={styles.deliveryFee}>
          <Text.Paragraph size="md" testID={CHECKOUT_ORDER_SUMMARY_DELIVERY_BASE_FEE}>
            Base Fee
          </Text.Paragraph>
          {baseFee === 0 ? (
            <Text.Paragraph size="md" style={{ color: tokens.colors.$success }}>
              FREE
            </Text.Paragraph>
          ) : (
            <Text.Paragraph size="md" weight="bold">
              ${(baseFee / 100).toFixed(2)}
            </Text.Paragraph>
          )}
        </View>

        {longDistance > 0 && (
          <View style={styles.deliveryFee}>
            <Text.Paragraph size="md" testID={CHECKOUT_ORDER_SUMMARY_DELIVERY_LONG_DISTANCE_FEE}>
              Long Distance Fee
            </Text.Paragraph>
            <Text.Paragraph size="md">${(longDistance / 100).toFixed(2)}</Text.Paragraph>
          </View>
        )}

        {geographical > 0 && (
          <View style={styles.deliveryFee}>
            <Text.Paragraph size="md" testID={CHECKOUT_ORDER_SUMMARY_DELIVERY_GEOGRAPHICAL_FEE}>
              Geographical Fee
            </Text.Paragraph>
            <Text.Paragraph size="md">${(geographical / 100).toFixed(2)}</Text.Paragraph>
          </View>
        )}
      </View>

      {showDeliveryFeesInfo && (
        <DeliveryFeesInfoModal onClose={() => setShowDeliveryFeesInfo(false)} />
      )}
    </>
  );
}

export default memo(DeliveryFees);

const styles = StyleSheet.create({
  deliveryFee: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2,
    paddingLeft: 20,
  },
  deliveryContainer: {
    flexDirection: 'column',
    marginHorizontal: 28,
    paddingBottom: 4,
  },
  skeleton: {
    height: 16,
    width: 48,
    borderRadius: 4,
  },
});
